import { useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./Legal.module.css";

const TermsConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles["legal-page"]}>
      <div className={styles["legal-container"]}>
        <Link to="/" className={styles["back-link"]}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5"></path>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Home
        </Link>

        <header className={styles["legal-header"]}>
          <h1>Terms &amp; Conditions</h1>
          <p className={styles["legal-date"]}>Last updated: May 12, 2026</p>
        </header>

        <div className={styles["legal-content"]}>
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using the WebTwin Platform ("Platform"), including all associated services,
              features, content, and 3D/XR experiences, you agree to be bound by these Terms &amp; Conditions
              ("Terms"). If you do not agree with any part of these Terms, you must not use our Platform.
            </p>
            <p> 
              These Terms constitute a legally binding agreement between you ("User," "you," or "your") and
              WebTwin Platform ("we," "us," or "our").
            </p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>
             WebTwin Platform provides a content management system (CMS) for creating, managing, and
              delivering immersive 3D, Unity WebGL, and XR experiences. Our services include but are not
              limited to:
            </p>
            <ul>
              <li>3D scene management and deployment</li>
              <li>Unity WebGL experience hosting and rendering</li>
              <li>Product and content management</li>
              <li>Interactive experience tools (hotspots, annotations, teleportation)</li>
              <li>User access control and permissions management</li>
              <li>Real-time publishing and content updates</li>
            </ul>
          </section>

          <section>
            <h2>3. User Accounts</h2>
            <h3>3.1 Account Registration</h3>
            <p>
              Certain features of the Platform require you to create an account. You agree to provide
              accurate, current, and complete information during registration and to update such information
              to keep it accurate and complete.
            </p>

            <h3>3.2 Account Security</h3>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials and for
              all activities that occur under your account. You agree to notify us immediately of any
              unauthorized access to or use of your account.
            </p>

            <h3>3.3 Account Roles</h3>
            <p>
              The Platform supports multiple user roles (e.g., Administrator, Editor, Viewer). Your access
              to features and content is determined by your assigned role. You agree not to attempt to
              access features beyond your authorized role.
            </p>
          </section>

          <section>
            <h2>4. Acceptable Use</h2>
            <p>You agree not to use the Platform to:</p>
            <ul>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon intellectual property rights of others</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
              <li>Interfere with or disrupt the Platform's infrastructure</li>
              <li>Reverse-engineer, decompile, or disassemble any part of the Platform</li>
              <li>Use automated tools to scrape, crawl, or extract data without permission</li>
              <li>Redistribute, sublicense, or resell access to the Platform without authorization</li>
              <li>Upload or distribute content that is unlawful, defamatory, or obscene</li>
            </ul>
          </section>

          <section>
            <h2>5. Intellectual Property</h2>
            <h3>5.1 Platform Ownership</h3>
            <p>
              The Platform, including its design, code, features, graphics, and all related intellectual
              property, is owned by WebTwin Platform and is protected by copyright, trademark, and other
              intellectual property laws. You may not copy, modify, or distribute any part of the Platform
              without our prior written consent.
            </p>

            <h3>5.2 User Content</h3>
            <p>
              You retain ownership of content you upload to the Platform (3D models, images, videos, etc.).
              By uploading content, you grant us a non-exclusive, worldwide, royalty-free license to host,
              store, display, and transmit your content solely for the purpose of operating and providing
              the Platform services.
            </p>

            <h3>5.3 Unity and Third-Party Content</h3>
            <p>
              3D scenes, Unity WebGL builds, and other third-party content used on the Platform are subject
              to their respective licenses. You are responsible for ensuring you have the necessary rights
              and licenses for any third-party content you upload.
            </p>
          </section>

          <section>
            <h2>6. Content Guidelines</h2>
            <p>
              All content uploaded, created, or managed through the Platform must comply with the following
              guidelines:
            </p>
            <ul>
              <li>Must not contain illegal, harmful, or offensive material</li>
              <li>Must not infringe on third-party intellectual property rights</li>
              <li>Must not include malware or malicious scripts</li>
              <li>Must comply with applicable export control laws</li>
              <li>Must not misrepresent the identity or origin of the content</li>
            </ul>
            <p>
              We reserve the right to remove any content that violates these guidelines without prior notice.
            </p>
          </section>

          <section>
            <h2>7. Service Availability</h2>
            <p>
              We strive to ensure the Platform is available at all times. However, we do not guarantee
              uninterrupted access and may temporarily suspend the Platform for maintenance, updates, or
              unforeseen circumstances. We are not liable for any loss or damage resulting from Platform
              downtime.
            </p>
          </section>

          <section>
            <h2>8. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by applicable law:
            </p>
            <ul>
              <li>
                The Platform is provided "as is" and "as available" without warranties of any kind, either
                express or implied.
              </li>
              <li>
                We shall not be liable for any indirect, incidental, special, consequential, or punitive
                damages arising out of or related to your use of the Platform.
              </li>
              <li>
                Our total liability for any claims arising under these Terms shall not exceed the amount you
                have paid us in the twelve (12) months preceding the claim.
              </li>
              <li>
                We are not responsible for the performance, accuracy, or suitability of Unity WebGL builds
                or third-party integrations used on the Platform.
              </li>
            </ul>
          </section>

          <section>
            <h2>9. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless WebTwin Platform and its officers,
              directors, employees, and agents from and against any claims, liabilities, damages, losses,
              and expenses (including reasonable legal fees) arising out of or related to your use of the
              Platform, your violation of these Terms, or your infringement of any third-party rights.
            </p>
          </section>

          <section>
            <h2>10. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your access to the Platform at any time, with or
              without cause and with or without notice, including if we believe you have violated these Terms.
              Upon termination:
            </p>
            <ul>
              <li>Your right to access the Platform ceases immediately</li>
              <li>We may delete your account and associated data</li>
              <li>Provisions that by their nature should survive termination will remain in effect</li>
            </ul>
          </section>

          <section>
            <h2>11. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction
              in which WebTwin Platform operates, without regard to its conflict of law provisions. Any
              disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts
              in that jurisdiction.
            </p>
          </section>

          <section>
            <h2>12. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Changes will be effective immediately
              upon posting to the Platform. Your continued use of the Platform after changes are posted
              constitutes your acceptance of the revised Terms. We encourage you to review these Terms
              periodically.
            </p>
          </section>

          <section>
            <h2>13. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall
              be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise
              remain in full force and effect.
            </p>
          </section>

          <section>
            <h2>14. Contact Us</h2>
            <p>
              If you have any questions about these Terms &amp; Conditions, please contact us at:
            </p>
            <div className={styles["contact-box"]}>
              <p><strong>WebTwin Platform</strong></p>
              <p>Email: 
                <a href="mailto:developer@saecons.com"> developer@saecons.com</a>
                </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
